import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import { getAuthHeaders } from '../../auth';
import { openFilePreview } from '../common/FilePreviewModal';
import Select2Dropdown from '../common/Select2Dropdown';
import ShippingLinePayablesForm from '../admin/ShippingLinePayablesForm';
import OtherPayablesForm from '../admin/OtherPayablesForm';
import PopoutWindow from '../common/PopoutWindow';
import TableScrollContainer from '../common/TableScrollContainer';
import './EIRReport.css';

const API_BASE = '/api/documentation/jobs';
const TABS = [
  ['pending', 'Pending'],
  ['waiting_eir', 'Waiting EIR'],
  ['pending_dem_damage', 'Pending Dem/Damage'],
  ['completed', 'Completed'],
  ['request_refund', 'Request Refund'],
];

const fmtDate = value => value ? new Date(String(value).length === 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-GB') : '—';
const fmtUpdated = value => value ? new Date(value).toLocaleString('en-GB') : '—';
const fmtDateTimeSplit = value => {
  if (!value) return null;
  const [date, time] = new Date(value).toLocaleString('en-GB').split(', ');
  return { date, time };
};
const todayInput = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};
const calculateDemurrage = (dropOffDate, returnDate) => {
  if (!dropOffDate) return null;
  const start = new Date(`${dropOffDate}T00:00:00`);
  const end = new Date(`${returnDate || todayInput()}T00:00:00`);
  return Math.round((end - start) / 86400000);
};

const getContainerDemurrage = (container, draft, editable) => {
  if (editable) {
    return draft.return_date ? calculateDemurrage(container.drop_off_expiry_date, draft.return_date) : null;
  }
  return container.demurrage ?? calculateDemurrage(container.drop_off_expiry_date, container.return_date);
};

export default function EIRReport() {
  const { hasPermission: rawHasPermission } = usePermissions();
  const hasPermission = useCallback((module, action) => module === 'EIR REPORTS'
    ? rawHasPermission('EIR REPORTS', action) || rawHasPermission('ADMIN EIR REPORTS', action)
    : rawHasPermission(module, action), [rawHasPermission]);
  const canCreate = hasPermission('EIR REPORTS', 'can_create');
  const canEdit = hasPermission('EIR REPORTS', 'can_edit');
  const canDelete = hasPermission('EIR REPORTS', 'can_delete');
  const [activeTab, setActiveTab] = useState('pending');
  const [showCompleted, setShowCompleted] = useState(true);
  const [showIncomplete, setShowIncomplete] = useState(true);
  const [showDemurrage, setShowDemurrage] = useState(true);
  const [showNoDemurrage, setShowNoDemurrage] = useState(true);
  const [showDamage, setShowDamage] = useState(true);
  const [showNoDamage, setShowNoDamage] = useState(true);
  const [searchField, setSearchField] = useState('idf_no');
  const [searchValue, setSearchValue] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchActiveIdx, setSearchActiveIdx] = useState(-1);
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedField, setAppliedField] = useState('idf_no');
  const [transporterFilter, setTransporterFilter] = useState('');
  const [tabData, setTabData] = useState({});
  const [rows, setRows] = useState([]);
  useEffect(() => {
    setShowCompleted(true);
    setShowIncomplete(true);
    setShowDemurrage(true);
    setShowNoDemurrage(true);
    setShowDamage(true);
    setShowNoDamage(true);
  }, [activeTab]);
  const [tabCounts, setTabCounts] = useState({});
  const [dropOffLocations, setDropOffLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});
  const [editing, setEditing] = useState({});
  const [drafts, setDrafts] = useState({});
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState({});
  const [savedContainerIds, setSavedContainerIds] = useState({});
  const [invoiceModal, setInvoiceModal] = useState({ open: false, idfNo: '', payables: [], loading: false, error: '' });
  const [openPayables, setOpenPayables] = useState([]);
  const [refundClearConfirm, setRefundClearConfirm] = useState(null);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedPageSize, setCompletedPageSize] = useState(25);

  const fetchRows = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const query = new URLSearchParams({ search_field: appliedField, search: appliedSearch });
    const tabs = TABS.map(([id]) => id);
    const otherTabs = tabs.filter(tab => tab !== activeTab);

    try {
      const activeResponse = await fetch(`${API_BASE}/eir-report/?tab=${activeTab}&${query}`, { headers: getAuthHeaders(false) });
      if (!activeResponse.ok) throw new Error('Failed to load EIR report.');
      const activePayload = await activeResponse.json();
      setTabData(prev => ({ ...prev, [activeTab]: activePayload }));
      setRows(activePayload.results || []);
      setTabCounts(prev => ({ ...prev, [activeTab]: activePayload.container_count || 0 }));
      setDropOffLocations(activePayload.drop_off_locations || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load EIR report.');
    } finally {
      if (!silent) setLoading(false);
    }

    if (otherTabs.length === 0) return;
    try {
      const responses = await Promise.all(otherTabs.map(tab => fetch(`${API_BASE}/eir-report/?tab=${tab}&${query}`, { headers: getAuthHeaders(false) })));
      if (responses.some(response => !response.ok)) return;
      const payloads = await Promise.all(responses.map(response => response.json()));
      const updates = Object.fromEntries(otherTabs.map((tab, index) => [tab, payloads[index]]));
      setTabData(prev => ({ ...prev, ...updates }));
      setTabCounts(prev => {
        const next = { ...prev };
        otherTabs.forEach((tab, index) => { next[tab] = payloads[index].container_count || 0; });
        return next;
      });
    } catch {
      // Background tab loads are non-critical; don't disrupt the active tab.
    }
  }, [activeTab, appliedSearch, appliedField]);

  const handleRefresh = () => {
    setSearchField('idf_no');
    setSearchValue('');
    setAppliedSearch('');
    setAppliedField('idf_no');
    setTransporterFilter('');
    setExpandedRows({});
    setExpandedDetails({});
    setShowCompleted(true);
    setShowIncomplete(true);
    setShowDemurrage(true);
    setShowNoDemurrage(true);
    setShowDamage(true);
    setShowNoDamage(true);
    fetchRows();
  };

  const handleViewPDF = () => {
    if (!releaseDateGroups.length) {
      toast.error('No data to export.');
      return;
    }
    const jsPDFCtor =
      (window && window.jspdf && window.jspdf.jsPDF) ||
      (window && window.jsPDF) ||
      null;
    const tabLabel = (TABS.find(([id]) => id === activeTab) || ['', 'EIR'])[1];
    const transporterName = transporterFilter
      ? (transporterOptions.find(o => o.id === transporterFilter) || {}).text || transporterFilter
      : '';
    const filenameBase = `EIR_${tabLabel.replace(/\s+/g, '_')}${transporterName ? `_${transporterName}` : ''}_Report`;

    const buildContainerRows = () => {
      const allRows = [];
      releaseDateGroups.forEach(group => {
        const releaseDates = group.rows
          .flatMap(row => (row.containers || []).map(c => c.release_date))
          .filter(Boolean);
        const earliestRelease = releaseDates.length
          ? releaseDates.reduce((min, d) => new Date(d) < new Date(min) ? d : min)
          : group.date || null;
        const daysSinceReleased = calculateDemurrage(earliestRelease, '');
        const dayLabel = daysSinceReleased !== null ? `${daysSinceReleased} ${daysSinceReleased > 1 ? 'DAYS' : 'DAY'}` : '-';
        const groupRows = [];
        group.rows.forEach(row => {
          (row.containers || []).forEach(container => {
            groupRows.push([
              row.idf_no || '—',
              row.importer || '—',
              row.bl_no || '—',
              container.container_no
                ? `${container.container_no}${container.size ? ` (${container.size}${container.container_type ? ' ' + container.container_type : ''})` : ''}`
                : '—',
              container.transporter || '—',
              fmtDate(container.release_date)
            ]);
          });
        });
        if (groupRows.length) {
          allRows.push({ dayLabel, daysSinceReleased, rows: groupRows });
        }
      });
      return allRows;
    };

    const groupedRows = buildContainerRows();
    if (!groupedRows.length) {
      toast.error('No data to export.');
      return;
    }

    if (!jsPDFCtor) {
      // Fallback printable HTML
      const w = window.open('', '_blank');
      if (!w) { toast.error('Popup blocked. Unable to open PDF.'); return; }
      const headerRow = ['IDF', 'Importer', 'BL#', 'Container #', 'Transporter', 'Released On'];
      const tables = groupedRows.map(g => `
        <table style="border-collapse:collapse;font-family:Arial;font-size:12px;width:100%;margin-bottom:16px;">
          <thead>
            <tr><th colspan="6" style="border:1px solid #ccc;padding:6px;background:#e5e5e5;text-align:center;font-weight:bold;">DAYS SINCE RELEASED: ${g.dayLabel}</th></tr>
            <tr>${headerRow.map(h => `<th style="border:1px solid #ccc;padding:4px;background:#333;color:#fff;text-align:left;">${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${g.rows.map(r => `<tr>${r.map(v => `<td style="border:1px solid #ccc;padding:4px;vertical-align:top;">${String(v).replace(/</g, '&lt;')}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      `).join('');
      w.document.write(`
        <html><head><title>${filenameBase}</title><meta charset="utf-8" /></head>
        <body style="margin:16px;font-family:Arial;">
          <h3 style="margin-bottom:4px;">${filenameBase.replace(/_/g, ' ')}</h3>
          ${tables}
          <script>window.print && window.print();</script>
        </body></html>
      `);
      w.document.close();
      return;
    }

    try {
      const doc = new jsPDFCtor({ orientation: 'l', unit: 'pt', format: 'a4' });
      const margin = 24;
      let startY = 40;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(filenameBase.replace(/_/g, ' ').toUpperCase(), margin, 20);
      doc.setFont(undefined, 'normal');

      groupedRows.forEach((group, index) => {
        if (index > 0) startY = doc.lastAutoTable.finalY + 24;
        const statusTextColor = group.daysSinceReleased === null
          ? [0, 0, 0]
          : group.daysSinceReleased >= 5
            ? [198, 40, 40]
            : group.daysSinceReleased >= 3
              ? [239, 108, 0]
              : [0, 0, 0];
        doc.autoTable({
          head: [
            [{ content: `DAYS SINCE RELEASED: ${group.dayLabel}`, colSpan: 6, styles: { halign: 'center', fontStyle: 'bold', fillColor: [229, 222, 241], textColor: statusTextColor } }],
            ['IDF', 'IMPORTER', 'BL#', 'CONTAINER #', 'TRANSPORTER', 'RELEASED ON']
          ],
          body: group.rows,
          styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak', valign: 'top', fillColor: [255, 255, 255] },
          headStyles: { fillColor: [229, 222, 241], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8 },
          alternateRowStyles: { fillColor: [227, 242, 253] },
          startY,
          margin,
          didDrawPage: (d) => {
            doc.setFontSize(10);
            doc.text(filenameBase.replace(/_/g, ' ').toUpperCase(), margin, 20);
            const pageStr = `Page ${d.pageNumber}`;
            const pageWidth = doc.internal.pageSize.getWidth();
            doc.text(pageStr, pageWidth - margin - doc.getTextWidth(pageStr), 20);
          }
        });
      });

      openFilePreview(doc.output('bloburl'), `${filenameBase}.pdf`);
    } catch (e) {
      console.warn('PDF generation error', e);
      toast.error('Failed to generate PDF.');
    }
  };

  const hasDetention = (obj) => {
    if (!obj) return false;
    if (typeof obj === 'boolean') return obj;
    if (Array.isArray(obj)) return obj.some(hasDetention);
    if (typeof obj.has_detention === 'boolean') return obj.has_detention;
    if (Array.isArray(obj.payables)) return obj.payables.some(hasDetention);
    return (obj.items || []).some(i => (i.charge_name || '').toLowerCase().includes('detention'));
  };

  const fetchPayables = async (idfNo) => {
    setInvoiceModal({ open: false, idfNo, payables: [], loading: true, error: '' });
    setOpenPayables([]);
    try {
      const res = await fetch(`/api/admin/payables-by-idf/?idf_no=${encodeURIComponent(idfNo)}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load payables.');
      const data = await res.json();
      const found = data.some(hasDetention);
      setInvoiceModal({ open: found, idfNo, payables: data, loading: false, error: '' });
    } catch (err) {
      setInvoiceModal(m => ({ ...m, error: err.message, loading: false }));
    }
  };

  const handleViewPayable = async (payable) => {
    const apiUrl = payable.type === 'shipping_line' ? '/api/admin/shipping-line-payables/' : '/api/admin/other-payables/';
    try {
      const res = await fetch(`${apiUrl}${payable.id}/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load payable details.');
      const data = await res.json();
      const windowId = `${payable.type}-${payable.id}-${Date.now()}`;
      const fullPayable = { ...data, _payable_source: payable.type === 'shipping_line' ? 'shipping' : 'other', _payable_type: payable.type };
      setOpenPayables(prev => [...prev, { payable: fullPayable, windowId }]);
    } catch (err) {
      toast.error(err.message || 'Failed to load payable details.');
    }
  };

  const closePayableWindow = (windowId) => {
    setOpenPayables(prev => prev.filter(entry => entry.windowId !== windowId));
  };

  useEffect(() => { fetchRows(); }, [activeTab, appliedSearch, appliedField]);

  useEffect(() => {
    if (searchValue.trim().length < 2) {
      setSearchOptions([]);
      return undefined;
    }
    if (searchValue.trim() === appliedSearch.trim() && searchField === appliedField) {
      return undefined;
    }
    const timeout = setTimeout(async () => {
      try {
        const query = new URLSearchParams({ search_field: searchField, search: searchValue.trim() });
        const response = await fetch(`${API_BASE}/eir-search-options/?${query}`, { headers: getAuthHeaders(false) });
        setSearchOptions(response.ok ? await response.json() : []);
      } catch {
        setSearchOptions([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchField, searchValue, appliedSearch, appliedField]);

  const startEdit = container => {
    setDrafts(current => ({ ...current, [container.id]: { return_date: container.return_date || '', damaged: !!container.damaged, demurrage_paid: !!container.demurrage_paid, damage_paid: !!container.damage_paid, kimemo: !!container.kimemo, eir_returned: !!container.eir_returned, eir_remarks: container.eir_remarks || '', drop_off_location: container.drop_off_location_value || '', req_refund: !!container.req_refund } }));
    setEditing(current => ({ ...current, [container.id]: true }));
  };

  const updateDraft = (id, field, value, defaults = {}) => setDrafts(current => ({ ...current, [id]: { ...(current[id] || defaults), [field]: value } }));
  const updateDraftFields = (id, values, defaults = {}) => setDrafts(current => ({ ...current, [id]: { ...(current[id] || defaults), ...values } }));
  const canModify = container => canEdit || (canCreate && !container.return_date && !container.kimemo && !container.eir_returned && !savedContainerIds[container.id]);
  const isEditing = container => editing[container.id] || (canCreate && !container.return_date && !container.kimemo && !container.eir_returned && !savedContainerIds[container.id]);
  const getDraft = container => drafts[container.id] || { return_date: container.return_date || '', damaged: !!container.damaged, demurrage_paid: !!container.demurrage_paid, damage_paid: !!container.damage_paid, kimemo: !!container.kimemo, eir_returned: !!container.eir_returned, eir_remarks: container.eir_remarks || '', drop_off_location: container.drop_off_location_value || '', req_refund: !!container.req_refund };

  const saveContainer = async (row, container, skipRefundForce = false) => {
    const draft = getDraft(container);

    if (activeTab === 'request_refund' && !!container.req_refund && !draft.req_refund && !skipRefundForce) {
      const hasProof = files[container.id]?.refund_proof_attachment || container.refund_proof_document;
      if (!hasProof) {
        setRefundClearConfirm({ row, container, mode: canDelete ? 'confirm' : 'require' });
        return;
      }
    }

    const demurrageDays = getContainerDemurrage(container, draft, true);
    // Auto-flag Request Refund for overpaid detention — but not from the
    // Completed tab, where a remarks-only save must keep the container completed.
    // An explicit manual clear of req_refund always wins over the auto-flag.
    if (activeTab !== 'completed' && !skipRefundForce && !(container.req_refund && !draft.req_refund) && demurrageDays !== null && demurrageDays < 0 && hasDetention(row)) {
      draft.req_refund = true;
    }

    if (draft.return_date && (draft.return_date > todayInput() || draft.return_date < container.release_date)) return toast.error('Return Date must be between Release Date and today.');
    if (!draft.drop_off_location) return toast.error('Drop-Off Location is required.');
    if (draft.eir_returned && !(draft.eir_remarks || '').trim()) return toast.error('Remarks required for EIR Returned.');
    if (draft.req_refund && !(draft.eir_remarks || '').trim()) return toast.error('Remarks are required for Request Refund.');
    if (draft.return_date && !draft.kimemo && !files[container.id]?.eir_attachment && !container.eir_document) return toast.error('EIR attachment is required when Return Date is entered and no KIMEMO.');
    if (draft.damaged && !files[container.id]?.gate_out_attachment && !container.gate_out_document) return toast.error('GATE-OUT INTERCHANGE attachment is required for damaged containers.');
    if (draft.kimemo && !draft.return_date) return toast.error('Return Date is required when Kimemo is selected.');
    if (draft.kimemo && !files[container.id]?.kimemo_attachment && !container.kimemo_document) return toast.error('KIMEMO attachment is required when Kimemo is selected.');

    setSaving(current => ({ ...current, [container.id]: true }));
    try {
      const form = new FormData();
      form.append('container_id', container.id);
      form.append('return_date', draft.return_date);
      form.append('damaged', draft.damaged);
      form.append('kimemo', draft.kimemo);
      form.append('eir_returned', draft.eir_returned);
      form.append('eir_remarks', (draft.eir_remarks || '').toUpperCase());
      form.append('req_refund', draft.req_refund);
      form.append('demurrage_paid', draft.demurrage_paid);
      form.append('damage_paid', draft.damage_paid);
      form.append('drop_off_location', draft.drop_off_location);
      for (const [key, file] of Object.entries(files[container.id] || {})) if (file) form.append(key, file);
      const isExistingEirEntry = editing[container.id] || savedContainerIds[container.id] || container.return_date || container.kimemo || container.eir_returned;
      const response = await fetch(`${API_BASE}/update-eir-container/`, { method: isExistingEirEntry ? 'PATCH' : 'POST', headers: getAuthHeaders(true), body: form });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to save container EIR details.');
      toast.success('Container EIR details saved.');
      setEditing(current => ({ ...current, [container.id]: false }));
      setSavedContainerIds(current => ({ ...current, [container.id]: true }));
      setFiles(current => ({ ...current, [container.id]: {} }));
      await fetchRows(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save container EIR details.');
    } finally {
      setSaving(current => ({ ...current, [container.id]: false }));
    }
  };

  const attachment = (container, key, document, label, editable) => {
    const selectedFile = files[container.id]?.[key];
    const previewFile = () => {
      // A newly-chosen file takes precedence so Replace previews the
      // selection, not the previously saved document.
      if (selectedFile) {
        openFilePreview(URL.createObjectURL(selectedFile), selectedFile.name);
      } else if (document) {
        const docUrl = document.document_file_url || document.document_file;
        // Cache-bust: replaced documents keep the same URL, so the browser
        // would otherwise serve the stale file.
        const busted = docUrl + (docUrl.includes('?') ? '&' : '?') + 'v=' + Date.now();
        openFilePreview(busted, label);
      }
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        {editable && <label style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', color: '#333', cursor: 'pointer', fontSize: 11 }}>
          Choose File
          <input type="file" style={{ display: 'none' }} onChange={event => setFiles(current => ({ ...current, [container.id]: { ...current[container.id], [key]: event.target.files[0] } }))} />
        </label>}
        {(document || selectedFile) && <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={previewFile}>View</button>}
        {editable && selectedFile && !document && <button type="button" className="dialog-btn dialog-btn-sm" onClick={() => setFiles(current => {
          const containerFiles = { ...(current[container.id] || {}) };
          delete containerFiles[key];
          return { ...current, [container.id]: containerFiles };
        })} title="Remove selected file">Remove</button>}
        {!document && !selectedFile && !editable && <span>—</span>}
      </div>
    );
  };

  const transporterOptions = useMemo(() => {
    const transporters = new Set();
    Object.values(tabData).forEach(payload => (payload.results || []).forEach(row => (row.containers || []).forEach(c => { if (c.transporter && c.transporter !== '—') transporters.add(c.transporter); })));
    return Array.from(transporters).sort().map(t => ({ id: t, text: t }));
  }, [tabData]);

  const displayRows = useMemo(() => {
    let filtered = rows;
    if (transporterFilter) {
      filtered = filtered
        .filter(row => (row.containers || []).some(c => c.transporter === transporterFilter))
        .map(row => ({ ...row, containers: (row.containers || []).filter(c => c.transporter === transporterFilter) }));
    }
    if (!appliedSearch.trim()) return filtered;
    const term = appliedSearch.trim().toLowerCase();
    if (appliedField === 'container_no') {
      return filtered
        .filter(row => (row.containers || []).some(c => (c.container_no || '').toLowerCase().includes(term)))
        .map(row => ({ ...row, containers: row.containers.filter(c => (c.container_no || '').toLowerCase().includes(term)) }));
    }
    if (appliedField === 'idf_no') {
      return filtered.filter(row => (row.idf_no || '').toLowerCase().includes(term));
    }
    if (appliedField === 'bl_no') {
      return filtered.filter(row => (row.bl_no || '').toLowerCase().includes(term));
    }
    return filtered;
  }, [rows, appliedSearch, appliedField, transporterFilter]);

  const filteredTabCounts = useMemo(() => {
    if (!transporterFilter) return tabCounts;
    const counts = {};
    TABS.forEach(([tab]) => {
      const results = tabData[tab]?.results || [];
      counts[tab] = results.reduce((sum, row) => sum + (row.containers || []).filter(c => c.transporter === transporterFilter).length, 0);
    });
    return counts;
  }, [tabData, transporterFilter, tabCounts]);

  const releaseDateGroups = useMemo(() => {
    const groupByIdf = activeTab === 'pending_dem_damage' || activeTab === 'completed' || activeTab === 'waiting_eir' || activeTab === 'request_refund';
    return Object.values(displayRows.reduce((groups, row) => {
      const key = groupByIdf ? (row.idf_no || `row-${row.id}`) : (row.report_date || 'unknown');
      if (!groups[key]) groups[key] = { key, date: row.report_date, label: row.group_label || null, rows: [] };
      groups[key].rows.push(row);
      return groups;
    }, {}));
  }, [displayRows, activeTab]);

  const totalPendingContainers = useMemo(() => releaseDateGroups.reduce((total, group) =>
    total + group.rows.reduce((rowTotal, row) =>
      rowTotal + (row.containers || []).filter(container => !container.return_date && !container.kimemo && !container.eir_returned).length
    , 0)
  , 0), [releaseDateGroups]);

  useEffect(() => {
    if (!appliedSearch.trim()) {
      setExpandedRows({});
      setExpandedDetails({});
      return;
    }
    const term = appliedSearch.trim().toLowerCase();
    const nextExpandedRows = {};
    const nextExpandedDetails = {};
    releaseDateGroups.forEach(group => {
      nextExpandedRows[group.key] = true;
      group.rows.forEach(row => {
        if (appliedField === 'container_no') {
          if ((row.containers || []).some(c => (c.container_no || '').toLowerCase().includes(term))) {
            nextExpandedDetails[row.id] = true;
          }
        } else if (appliedField === 'idf_no' || appliedField === 'bl_no') {
          const value = (appliedField === 'idf_no' ? row.idf_no : row.bl_no) || '';
          if (value.toLowerCase().includes(term)) {
            nextExpandedDetails[row.id] = true;
          }
        }
      });
    });
    setExpandedRows(nextExpandedRows);
    setExpandedDetails(nextExpandedDetails);
  }, [appliedSearch, appliedField, releaseDateGroups]);

  const showPaidColumn = activeTab === 'pending_dem_damage' || activeTab === 'completed' || activeTab === 'request_refund';
  const showCompletedDateColumn = activeTab === 'completed';
  const showDemDamage = activeTab === 'waiting_eir' || activeTab === 'pending_dem_damage'  || activeTab === 'completed' || activeTab === 'request_refund';
  const showRefundColumn = activeTab === 'pending_dem_damage' || activeTab === 'completed' || activeTab === 'request_refund';
  const getContainerCompletedDate = container => container.eir_updated_at || container.updated_at;
  const baseIdfHeaders = ['IDF', 'IMPORTER', 'BL #', '# CONTAINERS', '# CONT. RELEASED', '# CONT. BAL', '# I/CHANGE REC’D', '# I/CHANGE BALANCE'];
  const idfHeaders = [
    ...baseIdfHeaders,
    ...(showDemDamage ? ['DEMURRAGE', 'DAMAGE'] : []),
    ...(showRefundColumn ? ['REQ. REFUND'] : []),
    'VIEW INVOICES',
    ...(showCompletedDateColumn ? ['COMPLETED', 'COMPLETED DATE'] : []),
    'DETAILS'
  ];
  const filteredIdfRows = useMemo(() => {
    const flat = releaseDateGroups.flatMap(group => group.rows);
    let result = flat;
    if (activeTab === 'completed' && (!showCompleted || !showIncomplete)) {
      result = result.filter(row => {
        const completedCount = row.containers.filter(c => c.return_date && c.eir_document).length;
        const allCompleted = completedCount === row.total_containers && row.total_containers > 0;
        return showCompleted ? allCompleted : !allCompleted;
      });
    }
    if (showDemDamage && (!showDemurrage || !showNoDemurrage || !showDamage || !showNoDamage)) {
      result = result.filter(row => {
        const rowHasDemurrage = row.containers.some(c => (getContainerDemurrage(c, null, false) || 0) > 0);
        const rowHasDamage = row.containers.some(c => !!c.damaged);
        const demMatch = (showDemurrage && rowHasDemurrage) || (showNoDemurrage && !rowHasDemurrage);
        const damMatch = (showDamage && rowHasDamage) || (showNoDamage && !rowHasDamage);
        return demMatch && damMatch;
      });
    }
    return result;
  }, [activeTab, showCompleted, showIncomplete, showDemurrage, showNoDemurrage, showDamage, showNoDamage, releaseDateGroups]);

  useEffect(() => { setCompletedPage(1); }, [activeTab, showCompleted, showIncomplete, showDemurrage, showNoDemurrage, showDamage, showNoDamage, appliedSearch, transporterFilter]);

  const { pagedIdfRows, completedTotalPages, completedStart, completedPageClamped } = useMemo(() => {
    if (activeTab !== 'completed') {
      return { pagedIdfRows: filteredIdfRows, completedTotalPages: 1, completedStart: 0, completedPageClamped: 1 };
    }
    const totalPages = Math.max(1, Math.ceil(filteredIdfRows.length / completedPageSize));
    const pageClamped = Math.min(completedPage, totalPages);
    const start = (pageClamped - 1) * completedPageSize;
    return {
      pagedIdfRows: filteredIdfRows.slice(start, start + completedPageSize),
      completedTotalPages: totalPages,
      completedStart: start,
      completedPageClamped: pageClamped
    };
  }, [activeTab, filteredIdfRows, completedPage, completedPageSize]);

  const renderIdfTable = idfRows => (
    <TableScrollContainer>
    <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse', overflow: 'visible' }}>
      <thead><tr>{idfHeaders.map(header => <th key={header} style={{ background: '#82acfa', fontWeight: 700, border: '1px solid #b9c9d8', padding: 6, whiteSpace: 'normal', wordWrap: 'break-word', width: header.includes('I/CHANGE') ? 70 : undefined }}>
        {header === 'DEMURRAGE' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>DEMURRAGE</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <label title="Show Demurrage" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showDemurrage} onChange={e => setShowDemurrage(e.target.checked)} /> ✓
              </label>
              <label title="Show No Demurrage" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showNoDemurrage} onChange={e => setShowNoDemurrage(e.target.checked)} /> ✕
              </label>
            </div>
          </div>
        ) : header === 'DAMAGE' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>DAMAGE</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <label title="Show Damage" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showDamage} onChange={e => setShowDamage(e.target.checked)} /> ✓
              </label>
              <label title="Show No Damage" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showNoDamage} onChange={e => setShowNoDamage(e.target.checked)} /> ✕
              </label>
            </div>
          </div>
        ) : header === 'COMPLETED' && activeTab === 'completed' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div>COMPLETED</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <label title="Show Completed" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} /> ✓
              </label>
              <label title="Show Incomplete" style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={showIncomplete} onChange={e => setShowIncomplete(e.target.checked)} /> ✕
              </label>
            </div>
          </div>
        ) : (
          <div>{header}</div>
        )}
      </th>)}</tr></thead>
      <tbody>{idfRows.map((row, idx) => {
        const detailOpen = expandedDetails[row.id];
        const completedCount = row.containers.filter(c => c.return_date && c.eir_document).length;
        const allCompleted = completedCount === row.total_containers && row.total_containers > 0;
        const completedTimestamps = row.containers.map(getContainerCompletedDate).filter(Boolean);
        const idfCompletedTimestamp = completedTimestamps.length ? Math.max(...completedTimestamps.map(d => new Date(d).getTime())) : null;
        const idfCompletedDateSplit = fmtDateTimeSplit(idfCompletedTimestamp);
        const hasDemurrage = row.containers.some(c => (getContainerDemurrage(c, null, false) || 0) > 0);
        const hasDamage = row.containers.some(c => !!c.damaged);
        return <React.Fragment key={row.id}>
          <tr style={{ backgroundColor: idx % 2 === 0 ? '#e3f2fd' : '#fff' }}>
            {[row.idf_no, row.importer, row.bl_no, row.total_containers, row.released_containers, row.balance_containers, row.returned_containers, row.return_balance].map((value, index) => <td key={index} style={{ border: '1px solid #d4dce3', padding: 6, whiteSpace: 'normal', wordWrap: 'break-word', width: (index === 6 || index === 7) ? 70 : undefined }}>{value || '—'}</td>)}
            {showDemDamage && <>
              <td key="dem" style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><span style={{ color: hasDemurrage ? '#14833b' : '#c62828', fontWeight: 700 }}>{hasDemurrage ? '✓' : '✕'}</span></td>
              <td key="dam" style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><span style={{ color: hasDamage ? '#14833b' : '#c62828', fontWeight: 700 }}>{hasDamage ? '✓' : '✕'}</span></td>
            </>}
            {showRefundColumn && <td key="ref" style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><span style={{ color: row.req_refund ? '#14833b' : '#c62828', fontWeight: 700 }}>{row.req_refund ? '✓' : '✕'}</span></td>}
            <td key="inv" style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{(canEdit && hasDetention(row)) ? <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => fetchPayables(row.idf_no)}>View</button> : '—'}</td>
            {showCompletedDateColumn && <>
              <td key="comp" style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{allCompleted ? <span style={{ color: '#14833b', fontWeight: 700 }}>✓</span> : <span style={{ color: '#c62828', fontWeight: 700 }}>✕</span>}</td>
              <td key="compdate" style={{ border: '1px solid #d4dce3', padding: 6 }}>{idfCompletedDateSplit ? <><div style={{ fontWeight: 700 }}>{idfCompletedDateSplit.date}</div><div>{idfCompletedDateSplit.time}</div></> : '—'}</td>
            </>}
            <td key="det" style={{ border: '1px solid #d4dce3', padding: 6 }}><button type="button" className="icon-btn expand-btn" onClick={() => setExpandedDetails(current => ({ ...current, [row.id]: !detailOpen }))} title={detailOpen ? 'Collapse Details' : 'Expand Details'}><i className={`fa ${detailOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i></button></td>
          </tr>
          {detailOpen && <>
            <tr>
              <td colSpan={idfHeaders.length} style={{ padding: '8px 0 8px 16px', border: 'none' }}>
                <div style={{ borderLeft: '4px solid #f0ad4e', paddingLeft: 12 }}>{renderContainerTable(row)}</div>
              </td>
            </tr>
            <tr><td colSpan={idfHeaders.length} style={{ height: 8, border: 'none', backgroundColor: '#cecdcd' }} /></tr>
          </>}
        </React.Fragment>;
      })}</tbody>
    </table>
    </TableScrollContainer>
  );

  const renderContainerTable = row => (
    <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
      <table className="jobs-table" style={{ minWidth: 1450, borderCollapse: 'collapse' }}>
        <thead><tr>{['CONTAINER #', 'TRANSPORTER', 'RELEASED ON', 'DROP-OFF DATE', 'DROP-OFF LOC', 'RETURN DATE', 'EIR RETURNED', 'DAMAGED', ...(showRefundColumn ? ['REQ. REFUND'] : []), 'KIMEMO', 'DEMURRAGE', ...(showPaidColumn ? ['PAID'] : []), 'UPDATED BY', 'REMARKS', ...(showCompletedDateColumn ? ['COMPLETED DATE'] : []), 'ACTION'].map(header => <th key={header} style={{ background: '#fff4cc', fontWeight: 700, border: '1px solid #b9c9d8', padding: 6 }}>{header}</th>)}</tr></thead>
        <tbody>{row.containers.map(container => {
          const dropOffAttachmentUrl = container.drop_off_attachment_url || row.drop_off_attachment_url;
          const editable = isEditing(container) && canModify(container);
          const draft = getDraft(container);
          const demurrageDays = getContainerDemurrage(container, draft, editable);
          const rowClassName = appliedField === 'container_no' && appliedSearch && container.container_no?.toLowerCase().includes(appliedSearch.toLowerCase()) ? 'eir-search-match' : '';
          const rowTitle = '';
          const completedDateSplit = fmtDateTimeSplit(getContainerCompletedDate(container));
          return <tr key={container.id} className={rowClassName} title={rowTitle}>
            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{container.container_no}<br/>{container.size ? `(${container.size} ${container.container_type || ''})`.trim() : ''}</td><td style={{ border: '1px solid #d4dce3', padding: 6 }}>{container.transporter || '—'}</td><td style={{ border: '1px solid #d4dce3', padding: 6 }}>{fmtDate(container.release_date)}</td><td style={{ border: '1px solid #d4dce3', padding: 6 }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>{fmtDate(container.drop_off_expiry_date)}{container.drop_off_expiry_date && <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" disabled={!dropOffAttachmentUrl} title={dropOffAttachmentUrl ? 'View drop-off attachment' : 'No drop-off attachment available'} onClick={() => dropOffAttachmentUrl && openFilePreview(dropOffAttachmentUrl, 'Dropoff Attachment')}>View</button>}</div></td><td style={{ border: '1px solid #d4dce3', padding: 6 }}>{editable ? <Select2Dropdown options={dropOffLocations} value={draft.drop_off_location} onChange={value => updateDraft(container.id, 'drop_off_location', value, draft)} valueKey="id" displayKey="text" searchable /> : (container.drop_off_location || '—')}</td>
            <td style={{ border: '1px solid #d4dce3', padding: 6 }}><div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{editable ? <input type="date" min={container.release_date} max={todayInput()} value={draft.return_date} onChange={event => updateDraftFields(container.id, event.target.value ? { return_date: event.target.value, eir_returned: false, eir_remarks: '' } : { return_date: '' }, draft)} /> : fmtDate(container.return_date)}{attachment(container, 'eir_attachment', container.eir_document, 'EIR Attachment', editable)}</div></td>
            <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{editable ? <input type="checkbox" checked={draft.eir_returned} onChange={event => updateDraftFields(container.id, event.target.checked ? { eir_returned: true } : { eir_returned: false, eir_remarks: '' }, draft)} /> : <span>{container.eir_returned ? '✓' : '—'}</span>}</td>
            <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>{editable ? <input type="checkbox" checked={draft.damaged} onChange={event => updateDraft(container.id, 'damaged', event.target.checked, draft)} /> : <span style={{ color: container.damaged ? '#14833b' : '#c62828', fontWeight: 700 }}>{container.damaged ? '✓' : '✕'}</span>}{(editable ? draft.damaged : container.damaged) && attachment(container, 'gate_out_attachment', container.gate_out_document, 'GATE-OUT INTERCHANGE Attachment', editable)}</div></td>{showRefundColumn && <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{editable ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}><input type="checkbox" checked={draft.req_refund} onChange={event => updateDraft(container.id, 'req_refund', event.target.checked, draft)} />{(container.refund_proof_document || (container.req_refund && !draft.req_refund)) && attachment(container, 'refund_proof_attachment', container.refund_proof_document, 'REFUND PROOF', editable)}</div> : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}><span style={{ color: container.req_refund ? '#14833b' : '#c62828', fontWeight: 700 }}>{container.req_refund ? '✓' : '✕'}</span>{container.refund_proof_document && attachment(container, 'refund_proof_attachment', container.refund_proof_document, 'REFUND PROOF', false)}</div>}</td>}
            <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>{editable ? <input type="checkbox" checked={draft.kimemo} onChange={event => updateDraft(container.id, 'kimemo', event.target.checked, draft)} /> : <span style={{ color: container.kimemo ? '#14833b' : '#c62828', fontWeight: 700 }}>{container.kimemo ? '✓' : '✕'}</span>}{(editable ? draft.kimemo : container.kimemo) && attachment(container, 'kimemo_attachment', container.kimemo_document, 'KIMEMO Attachment', editable)}</div></td>
            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{demurrageDays ?? '—'}</td>
            {showPaidColumn && <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {demurrageDays > 0 && (
                  <div>{editable ? <input type="checkbox" checked={draft.demurrage_paid} onChange={event => updateDraft(container.id, 'demurrage_paid', event.target.checked, draft)} /> : <span style={{ color: container.demurrage_paid ? '#14833b' : '#c62828', fontWeight: 700 }}>{container.demurrage_paid ? '✓' : '✕'}</span>} DEMURRAGE</div>
                )}
                {(editable ? draft.damaged : container.damaged) && (
                  <div>{editable ? <input type="checkbox" checked={draft.damage_paid} onChange={event => updateDraft(container.id, 'damage_paid', event.target.checked, draft)} /> : <span style={{ color: container.damage_paid ? '#14833b' : '#c62828', fontWeight: 700 }}>{container.damage_paid ? '✓' : '✕'}</span>} DAMAGE</div>
                )}
              </div>
            </td>}
            <td style={{ border: '1px solid #d4dce3', padding: 6 }} title={container.updated_at ? fmtUpdated(container.updated_at) : ''}>{container.updated_by || '—'}</td>
            <td style={{ border: '1px solid #d4dce3', padding: 6, maxWidth: 180 }}>{editable ? <textarea value={draft.eir_remarks} onChange={event => updateDraft(container.id, 'eir_remarks', event.target.value, draft)} rows={2} style={{ minWidth: 120, maxWidth: '100%' }} /> : <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95em' }}>{container.eir_remarks || '—'}</div>}</td>
            {showCompletedDateColumn && <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{completedDateSplit ? <><div style={{ fontWeight: 700 }}>{completedDateSplit.date}</div><div>{completedDateSplit.time}</div></> : '—'}</td>}
            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{editable ? <div style={{ display: 'flex', gap: 4 }}><button type="button" disabled={saving[container.id]} onClick={() => saveContainer(row, container)} style={{ padding: '8px 16px', border: 'none', backgroundColor: '#007bff', color: 'white', borderRadius: 4, cursor: saving[container.id] ? 'not-allowed' : 'pointer', fontSize: 11 }}>{saving[container.id] ? 'Saving...' : 'Save'}</button>{activeTab !== 'pending' && <button type="button" disabled={saving[container.id]} onClick={() => { setEditing(current => ({ ...current, [container.id]: false })); setDrafts(current => { const next = { ...current }; delete next[container.id]; return next; }); setFiles(current => ({ ...current, [container.id]: {} })); }} style={{ padding: '8px 16px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#333', borderRadius: 4, cursor: saving[container.id] ? 'not-allowed' : 'pointer', fontSize: 11 }}>Cancel</button>}</div> : canEdit && (container.return_date || container.kimemo || container.eir_returned || savedContainerIds[container.id]) ? <button type="button" className="icon-btn edit-btn" onClick={() => startEdit(container)} title="Edit"><i className="fa fa-pencil-alt"></i></button> : '—'}</td>
          </tr>;
        })}</tbody>
      </table>
    </div>
  );

  const renderRefundClearConfirm = () => {
    if (!refundClearConfirm) return null;
    const { row, container, mode } = refundClearConfirm;
    return (
      <div className="modal-overlay">
        <div className="dialog-container" style={{ width: '480px', maxWidth: '95%' }}>
          <div className="dialog-header" style={{ background: '#c62828', color: '#fff', borderRadius: '4px 4px 0 0' }}>
            <h3 style={{ margin: 0, color: '#fff' }}>Clear Request Refund</h3>
          </div>
          <div className="dialog-body" style={{ padding: '20px' }}>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              {mode === 'require'
                ? 'Please attach proof of refund'
                : 'Proof of Refund is not attached. Are you sure you would like to proceed?'}
            </p>
          </div>
          <div className="dialog-footer">
            {mode === 'require' ? (
              <button
                className="dialog-btn dialog-btn-primary"
                onClick={() => setRefundClearConfirm(null)}
                disabled={saving[container.id]}
              >
                OK
              </button>
            ) : (
              <>
                <button
                  className="dialog-btn dialog-btn-primary"
                  onClick={() => { setRefundClearConfirm(null); saveContainer(row, container, true); }}
                  disabled={saving[container.id]}
                >
                  Yes
                </button>
                <button
                  className="dialog-btn dialog-btn-secondary"
                  onClick={() => setRefundClearConfirm(null)}
                  disabled={saving[container.id]}
                >
                  No
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderInvoiceModal = () => {
    if (!invoiceModal.open) return null;
    const detentionRows = invoiceModal.payables.filter(hasDetention).sort((a, b) => {
      if (!a.payment_date && !b.payment_date) return 0;
      if (!a.payment_date) return 1;
      if (!b.payment_date) return -1;
      return new Date(a.payment_date) - new Date(b.payment_date);
    });
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ backgroundColor: '#fff', borderRadius: 8, width: '100%', maxWidth: 800, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Invoices for IDF {invoiceModal.idfNo}</h3>
            <button type="button" onClick={() => setInvoiceModal(m => ({ ...m, open: false }))} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
          </div>
          {invoiceModal.loading ? <div className="loading">Loading...</div> : invoiceModal.error ? <div style={{ color: '#c62828' }}>{invoiceModal.error}</div> : !detentionRows.length ? <div className="no-data">No detention payables found.</div> : (
            <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['DATE PAID', 'DETENTION CHARGE', 'DETENTION INVOICE'].map(h => <th key={h} style={{ background: '#e5def1', fontWeight: 700, border: '1px solid #b9c9d8', padding: 6 }}>{h}</th>)}</tr></thead>
              <tbody>{detentionRows.map(p => {
                const detentionItems = (p.items || []).filter(i => (i.charge_name || '').toLowerCase().includes('detention'));
                const chargeAmount = detentionItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
                return (
                  <tr key={p.id}>
                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{fmtDate(p.payment_date)}</td>
                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{detentionItems.length ? Number(chargeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                    <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{hasDetention(p) ? <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => handleViewPayable(p)}>View</button> : '—'}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="report-container eir-report" style={{ padding: '16px 16px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 0 }}>
        <h2 style={{ margin: 0 }}>EIR Reports</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {(activeTab === 'pending' || activeTab === 'waiting_eir') && (
            <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleViewPDF} disabled={loading}><i className="fa fa-file-pdf-o" /> View PDF</button>
          )}
          <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleRefresh} disabled={loading}><i className="fa fa-refresh" /> Refresh</button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, flexWrap: 'nowrap' }}>
        <div style={{ width: 140, flex: '0 0 140px' }}>
          <Select2Dropdown
            options={[{ id: 'idf_no', text: 'IDF NO.' }, { id: 'bl_no', text: 'BL NO.' },  { id: 'container_no', text: 'CONTAINER NO.' }]}
            value={searchField}
            onChange={value => { setSearchField(value); setSearchOptions([]); }}
            valueKey="id"
            displayKey="text"
            searchable={false}
            clearable={false}
          />
        </div>
        <div style={{ position: 'relative', width: 180, flex: '0 0 180px' }}>
          <input value={searchValue} onChange={event => { setSearchValue(event.target.value); setSearchActiveIdx(-1); }} onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setSearchActiveIdx(i => Math.min(i + 1, searchOptions.length - 1)); } else if (event.key === 'ArrowUp') { event.preventDefault(); setSearchActiveIdx(i => Math.max(i - 1, 0)); } else if (event.key === 'Enter') { if (searchActiveIdx >= 0) { event.preventDefault(); const opt = searchOptions[searchActiveIdx]; setSearchValue(opt.text); setSearchOptions([]); setSearchActiveIdx(-1); } else { setAppliedSearch(searchValue.trim()); setAppliedField(searchField); setSearchOptions([]); } } else if (event.key === 'Escape') { setSearchOptions([]); setSearchActiveIdx(-1); } }} placeholder="SEARCH..." style={{ fontSize: 12, padding: '3px 28px 3px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%', height: 28, boxSizing: 'border-box', background: '#fff', color: '#222' }} />
          <button type="button" onClick={() => { setAppliedSearch(searchValue.trim()); setAppliedField(searchField); setSearchOptions([]); }} title="Search" style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', fontSize: 13, padding: '0 4px' }}><i className="fa fa-search" /></button>
          <button type="button" onClick={() => { setSearchValue(''); setAppliedSearch(''); setSearchOptions([]); }} title="Clear search" style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', fontSize: 13, padding: '0 4px' }}><i className="fa fa-times" /></button>
          {searchOptions.length > 0 && <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, width: '100%', background: '#fff', border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.18)', zIndex: 1000, maxHeight: 240, overflowY: 'auto' }}>
            {searchOptions.map((option, idx) => <button type="button" key={option.id} onClick={() => { setSearchValue(option.text); setSearchOptions([]); setSearchActiveIdx(-1); }} onMouseEnter={() => setSearchActiveIdx(idx)} style={{ display: 'block', width: '100%', padding: '6px 8px', textAlign: 'left', background: idx === searchActiveIdx ? '#0d6efd' : 'transparent', color: idx === searchActiveIdx ? '#fff' : '#222', border: 'none', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 12 }}>{option.text}</button>)}
          </div>}
        </div>
        <div style={{ width: 200, flex: '0 0 200px' }}>
          <Select2Dropdown
            options={transporterOptions}
            value={transporterFilter}
            onChange={setTransporterFilter}
            valueKey="id"
            displayKey="text"
            clearable
            placeholder="Filter by transporter..."
            searchable
          />
        </div>
      </div>
      <div className="custom-tabs-container" style={{ marginTop: 16, marginBottom: 16 }}>
        {TABS.map(([id, label]) => <button type="button" key={id} className={`custom-tab ${activeTab === id ? 'active' : ''}`} style={id === 'request_refund' ? { color: activeTab === id ? '#fff' : '#c62828', backgroundColor: activeTab === id ? '#c62828' : undefined } : {}} onClick={() => setActiveTab(id)}>{label} ({filteredTabCounts[id] || 0})</button>)}
      </div>
      {loading ? <div className="loading">Loading...</div> : activeTab === 'pending' ? (
        releaseDateGroups.length === 0 ? <div className="no-data">No records found.</div> : (
          <TableScrollContainer>
          <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse', overflow: 'visible' }}>
            <thead><tr>{['DATE', 'DAYS SINCE RELEASED', `# CONTAINERS INTERCHANGE PENDING - ${totalPendingContainers}`, 'TRANSPORTER', 'DETAILS'].map(header => <th key={header} style={{ background: '#e5def1', fontWeight: 700, fontSize: '1em', border: '1px solid #b9c9d8', padding: 6 }}><div>{header}</div></th>)}</tr></thead>
            <tbody>{releaseDateGroups.map((group, idx) => {
              const groupKey = group.key;
              const groupOpen = expandedRows[groupKey];
              const daysSinceReleased = calculateDemurrage(group.date, '');
              const pendingContainers = group.rows.reduce((total, row) => total + row.containers.filter(container => !container.return_date && !container.kimemo && !container.eir_returned).length, 0);
              const groupTransporters = Array.from(new Set(
                group.rows
                  .flatMap(row => row.containers || [])
                  .map(container => container.transporter)
                  .filter(t => t && t !== '—')
              )).sort().join(' | ') || '—';
              return <React.Fragment key={groupKey}>
                <tr onClick={() => setExpandedRows(current => ({ ...current, [groupKey]: !groupOpen }))} style={{ backgroundColor: idx % 2 === 0 ? '#f3e5f5' : '#fff', cursor: 'pointer' }}>
                  <td style={{ border: '1px solid #d4dce3', padding: 6, fontWeight: 700, fontSize: '1.15em' }}>{fmtDate(group.date)}</td>
                  <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center', fontSize: '1.15em' }} className={daysSinceReleased !== null && daysSinceReleased >= 5 ? 'eir-release-status-alert-red' : daysSinceReleased !== null && daysSinceReleased >= 3 ? 'eir-release-status-alert-orange' : 'eir-release-status'}>
                    {daysSinceReleased ?? 0} {daysSinceReleased > 1 ? 'DAYS' : 'DAY'}
                  </td>
                  <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center', fontSize: '1.15em' }} className='eir-release-status'>
                    {pendingContainers} {pendingContainers > 0 ? 'CONTAINER' : 'CONTAINERS'}
                  </td>
                  <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center', fontSize: '1.15em' }}>{groupTransporters}</td>
                  <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}><button type="button" className="icon-btn expand-btn" onClick={event => { event.stopPropagation(); setExpandedRows(current => ({ ...current, [groupKey]: !groupOpen })); }} title={groupOpen ? 'Collapse Details' : 'Expand Details'}><i className={`fa ${groupOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i></button></td>
                </tr>
                {groupOpen && <>
                  <tr>
                    <td colSpan={5} style={{ padding: '8px 0 8px 16px', border: 'none' }}>
                      <div style={{ borderLeft: '4px solid #8e24aa', paddingLeft: 12 }}>{renderIdfTable(group.rows)}</div>
                    </td>
                  </tr>
                  <tr><td colSpan={5} style={{ height: 8, border: 'none', backgroundColor: '#cecdcd' }} /></tr>
                </>}
              </React.Fragment>;
            })}</tbody>
          </table>
          </TableScrollContainer>
        )
      ) : (
        <>
          {activeTab === 'completed' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#e3f2fd', padding: '8px 12px', borderRadius: 6, marginBottom: 8, marginTop: 16 }}>
              <div style={{ fontWeight: 'bold' }}>COMPLETED</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ color: '#555' }}>Rows per page:</span>
                {[25, 50, 100].map(size => (
                  <button key={size} onClick={() => { setCompletedPageSize(size); setCompletedPage(1); }}
                    style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: 'pointer', fontWeight: completedPageSize === size ? 'bold' : 'normal', background: completedPageSize === size ? '#007bff' : '#fff', color: completedPageSize === size ? '#fff' : '#333' }}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}
          {pagedIdfRows.length === 0 ? <div className="no-data">No records found.</div> : renderIdfTable(pagedIdfRows)}
          {activeTab === 'completed' && completedTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8, fontSize: 13 }}>
              <span style={{ color: '#555' }}>
                {completedStart + 1}–{Math.min(completedStart + completedPageSize, filteredIdfRows.length)} of {filteredIdfRows.length}
              </span>
              <button onClick={() => setCompletedPage(p => Math.max(1, p - 1))} disabled={completedPageClamped === 1}
                style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: completedPageClamped === 1 ? 'default' : 'pointer', background: '#fff', color: completedPageClamped === 1 ? '#bbb' : '#333' }}>
                ‹ Prev
              </button>
              <span style={{ color: '#555' }}>Page {completedPageClamped} / {completedTotalPages}</span>
              <button onClick={() => setCompletedPage(p => Math.min(completedTotalPages, p + 1))} disabled={completedPageClamped === completedTotalPages}
                style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: completedPageClamped === completedTotalPages ? 'default' : 'pointer', background: '#fff', color: completedPageClamped === completedTotalPages ? '#bbb' : '#333' }}>
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    {renderInvoiceModal()}
    {renderRefundClearConfirm()}
    {openPayables.map(({ payable, windowId }) => (
      <PopoutWindow
        key={windowId}
        title={`Payable - ${payable.reference_no || payable.invoice_no || payable.id}`}
        name={windowId}
        onClose={() => closePayableWindow(windowId)}
      >
        {payable._payable_source === 'shipping' ? (
          <ShippingLinePayablesForm
            mode="view"
            initialData={payable}
            onClose={() => closePayableWindow(windowId)}
            onSave={() => closePayableWindow(windowId)}
            showViewExpenses={false}
            module="SD"
            popoutMode={true}
          />
        ) : (
          <OtherPayablesForm
            mode="view"
            initialData={payable}
            onClose={() => closePayableWindow(windowId)}
            onSave={() => closePayableWindow(windowId)}
            showViewExpenses={false}
            module={payable.module || 'PERMIT'}
            popoutMode={true}
          />
        )}
      </PopoutWindow>
    ))}
    </div>
  );
}
